/**
 * Allegati della chat WhatsApp: tipo di invio, file accettati, limiti di dimensione.
 * Funzioni pure (test in __tests__/attachments.test.ts).
 *
 * Limiti WhatsApp (Cloud API, "Supported media types", developers.facebook.com):
 * immagini 5 MB, video e audio 16 MB, documenti 100 MB. Per i numeri Standard (WAHA)
 * i limiti del telefono sono simili: usiamo gli stessi, cosi' il commerciale non scopre
 * l'errore solo dopo l'invio.
 */

export type AttachmentKind = 'image' | 'video' | 'audio' | 'document';

export const DOCUMENT_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'zip', 'rar', '7z', 'odt', 'ods', 'odp', 'rtf',
];

/** Valore per <input accept> dei documenti. */
export const DOCUMENT_ACCEPT = DOCUMENT_EXTENSIONS.map((e) => `.${e}`).join(',');

const MB = 1024 * 1024;
export const MAX_BYTES: Record<AttachmentKind, number> = {
  image: 5 * MB,
  video: 16 * MB,
  audio: 16 * MB,
  document: 100 * MB,
};

// Solo i formati che WhatsApp mostra come foto/video (webp = sticker, mov non supportato).
const IMAGE_SENT_AS_PHOTO = new Set(['image/jpeg', 'image/png']);
const VIDEO_SENT_AS_VIDEO = new Set(['video/mp4', 'video/3gpp']);

/**
 * Come inviare il file. Tutto il resto (gif, heic, mov, file audio...) va come documento:
 * un file audio inviato come 'audio' partirebbe come nota vocale.
 */
export function attachmentKind(file: { type: string; name: string }): AttachmentKind {
  const mime = (file.type || '').toLowerCase();
  if (IMAGE_SENT_AS_PHOTO.has(mime)) return 'image';
  if (VIDEO_SENT_AS_VIDEO.has(mime)) return 'video';
  return 'document';
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MB) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / MB).toFixed(1).replace('.', ',')} MB`;
}

/** Messaggio d'errore leggibile se il file non si puo' inviare, altrimenti null. */
export function validateAttachment(file: { type: string; name: string; size: number }): string | null {
  if (file.size === 0) return `"${file.name}" e' vuoto`;
  const kind = attachmentKind(file);
  if (file.size > MAX_BYTES[kind]) {
    const label = { image: 'le foto', video: 'i video', audio: 'gli audio', document: 'i documenti' }[kind];
    return `"${file.name}" pesa ${formatBytes(file.size)}: WhatsApp accetta al massimo ${formatBytes(MAX_BYTES[kind])} per ${label}`;
  }
  return null;
}

/** File da una lista del browser (drop, incolla, input), ignorando le cartelle. */
export function filesFromList(list: FileList | File[] | null | undefined): File[] {
  if (!list) return [];
  return Array.from(list).filter((f) => f && (f.size > 0 || f.type));
}

/** Le immagini incollate arrivano come "image.png": nome con data e ora, piu' leggibile. */
export function renamePastedFile(file: File, now: Date = new Date()): File {
  if (!/^image\.\w+$/i.test(file.name)) return file;
  const ext = file.name.split('.').pop();
  const pad = (n: number) => String(n).padStart(2, '0');
  const name = `immagine-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.${ext}`;
  return new File([file], name, { type: file.type, lastModified: file.lastModified });
}
